/**
 * MEMBERSHIPS — Meet ARVEXIS, Essential and Premium (Founder-approved final design).
 * Every number is read from /api/subscription/plans, the same configuration the gates enforce.
 */
import React from 'react';
import { List, ChevronDown } from 'lucide-react';
import { MEMBERSHIP_COPY, ROW_KEYS } from './membershipCopy';
import { MembershipCard, ROW_ICONS } from './MembershipCard';
import { MembershipInfo, MembershipPillars } from './MembershipExtras';
import '../styles/membership-plans.css';

const fmt = (n) => (Number.isInteger(n) ? `$${n}` : `$${Number(n).toFixed(2)}`);

const PlanDetails = ({ c, plans, days, nf }) => {
  const tiers = [['meet', plans.trial, c.nextMeet], ['essential', plans.essential, c.nextEssential],
    ['premium', plans.premium, c.nextPremium]];
  const cell = (id, k, v) => (
    <td key={id} className={id === 'premium' ? 'mp-td--prem' : ''} data-testid={`plan-details-${id}-${k}`}>{v}</td>
  );
  return (
    <details className="mp-compare" open data-testid="plan-details">
      <summary className="mp-compare__head" data-testid="plan-details-toggle">
        <List className="mp-row__icon" aria-hidden="true" /><span>{c.compare}</span>
        <ChevronDown className="mp-compare__chev" aria-hidden="true" />
      </summary>
      <div className="mp-compare__scroll">
        <table className="mp-table">
          <thead><tr>
            <th scope="col">{c.feature}</th><th scope="col">{c.trialCol(days)}</th>
            <th scope="col">{c.essential}</th><th scope="col" className="mp-td--prem">{c.premium}</th>
          </tr></thead>
          <tbody>
            {[...ROW_KEYS, 'next_room'].map((k) => {
              const Icon = ROW_ICONS[k];
              return (
                <tr key={k}>
                  <th scope="row"><span className="mp-table__cap"><Icon className="mp-row__icon" aria-hidden="true" />{c.table[k]}</span></th>
                  {tiers.map(([id, tier, next]) => cell(id, k,
                    k === 'next_room' ? next : (tier[k] > 0 ? nf.format(tier[k]) : c.none)))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mp-compare__note" data-testid="plan-details-trial-terms">{c.meetTerms(days)}</p>
      <p className="mp-compare__note" data-testid="plan-details-research">{c.research}</p>
    </details>
  );
};

export const MembershipPlans = ({ lang, plans, storePrices, canStartTrial, isTrial, onStartTrial, onChoose, busy }) => {
  const c = MEMBERSHIP_COPY[lang === 'es' ? 'es' : 'en'];
  if (!plans) return null;
  const days = plans.trial_days || 7;
  const nf = new Intl.NumberFormat(lang === 'es' ? 'es-ES' : 'en-US');
  // iPhone: the App Store's localized price for the member's storefront; web: the backend price.
  const essentialPrice = storePrices?.essential || fmt(plans.essential_price);
  const premiumPrice = storePrices?.premium || fmt(plans.monthly_price);
  const button = (label, onClick, testid, kind, disabled = false) => (
    <button type="button" onClick={onClick} disabled={busy || disabled} className={`mp-btn mp-btn--${kind}`} data-testid={testid}>
      {busy && !disabled ? c.working : label}
    </button>
  );
  const trialLabel = canStartTrial ? c.start(days) : (isTrial ? c.trialActive : c.trialUsed);
  return (
    <div className="mp" data-testid="membership-plans">
      <div className="mp-grid">
        <MembershipCard c={c} id="meet" tier={plans.trial} title={c.meet} sub={c.meetSub} nf={nf}
          badge={c.trialBadge(days)} fine={canStartTrial ? c.meetFine : c.meetUsedFine}
          action={button(trialLabel, onStartTrial, 'start-trial-btn', 'gold', !canStartTrial)} />
        <MembershipCard c={c} id="essential" tier={plans.essential} title={c.essential} sub={c.essentialSub}
          price={essentialPrice} per={c.month} nf={nf} fine={c.renewFine(essentialPrice)}
          action={button(c.choose(c.essential), () => onChoose('essential'), 'choose-essential-btn', 'outline')} />
        <MembershipCard c={c} id="premium" tier={plans.premium} title={c.premium} sub={c.premiumSub}
          price={premiumPrice} per={c.month} nf={nf} badge={c.mostRoom} featured fine={c.renewFine(premiumPrice)}
          action={button(c.choose(c.premium), () => onChoose('premium'), 'choose-premium-btn', 'gold')} />
      </div>
      <div className="mp-panel">
        <PlanDetails c={c} plans={plans} days={days} nf={nf} />
        <MembershipInfo c={c} plans={plans} />
      </div>
      <MembershipPillars c={c} />
    </div>
  );
};

export default MembershipPlans;
